import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  normalizeOptions,
  isDateLike,
  type CustomField,
  type CustomFieldInput,
  type CustomFieldSettings,
  type CustomFieldType,
  type CustomFieldValidation,
  type SelectOption,
} from "@/lib/service-detail";
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from "./Drawer";
import { Tabs } from "./Tabs";
import { EmptyState } from "./EmptyState";
import { useToast } from "./Toast";

/**
 * Right-rail Custom Field settings panel — Pencil `Ah0ns` (`v2/Service —
 * Custom Fields`). Drawer + tabs (Basic / Options / Advanced / Validation)
 * matches the chrome family used by the booking + inventory + client
 * detail panels.
 *
 * The Options tab is only visible for `select`-type fields. The Validation
 * tab swaps its body by type — pattern (text), min/max (number), file
 * types + size (file), and an empty state for date/checkbox.
 *
 * Save bundles label + type + required + options + settings into one
 * PATCH so a single round-trip atomically updates the field.
 */

const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Textarea" },
  { value: "number", label: "Number" },
  { value: "select", label: "Select" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date and Time" },
  { value: "file", label: "File" },
  { value: "checkbox", label: "Checkbox" },
];

type TabKey = "basic" | "options" | "advanced" | "validation" | "scope";

/**
 * Carries scope-related draft state for the optional Scope tab. Picks
 * are kept in a single object so callers can render whichever pickers
 * fit their scope (customer = tag + client; inventory = category).
 * Whatever's non-undefined here is merged into the save payload.
 */
export type ScopeDraft = {
  scopeTagIds?: string[];
  scopeClientIds?: string[];
  scopeCategories?: string[];
};

export type ScopeBody = {
  /** Label for the Scope tab itself (defaults to "Scope"). */
  tabLabel?: string;
  /** Optional helper text shown above the picker(s). */
  helper?: string;
  /** Renders the picker(s) bound to the panel's scope draft state. */
  render: (props: {
    field: CustomField;
    draft: ScopeDraft;
    setDraft: (next: ScopeDraft) => void;
  }) => React.ReactNode;
};

export function CustomFieldPanel({
  field,
  onClose,
  onSave,
  onSaveSuccess,
  scopeBody,
  primaryDateTakenBy,
}: {
  field: CustomField | null;
  onClose: () => void;
  /** Persist the edited field. The caller owns the API binding (per-service vs
   *  tenant-wide vs inventory) so this drawer stays scope-agnostic. */
  onSave: (body: Partial<CustomFieldInput>) => Promise<CustomField>;
  /** Called after a successful save — typical use is cache invalidation. */
  onSaveSuccess?: () => void;
  /** Optional Scope tab. When omitted (e.g. per-service custom fields), no
   *  Scope tab is rendered. When supplied, the panel renders the tab and
   *  merges the picker's draft into the save payload. */
  scopeBody?: ScopeBody;
  /** Label of ANOTHER date field on the same service that already owns the
   *  "booking date" flag, if any. When set, this field's toggle is disabled
   *  (only one booking-date field per service). */
  primaryDateTakenBy?: string | null;
}) {
  const open = !!field;
  const toast = useToast();

  const [tab, setTab] = useState<TabKey>("basic");
  const [label, setLabel] = useState("");
  const [type, setType] = useState<CustomFieldType>("text");
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [placeholder, setPlaceholder] = useState("");
  const [helpText, setHelpText] = useState("");
  const [defaultValue, setDefaultValue] = useState("");
  const [validation, setValidation] = useState<CustomFieldValidation>({});
  const [primaryDate, setPrimaryDate] = useState(false);
  const [showOnCalendar, setShowOnCalendar] = useState(false);
  const [scopeDraft, setScopeDraft] = useState<ScopeDraft>({});

  // Reset form state every time a different field opens.
  useEffect(() => {
    if (!field) return;
    setTab("basic");
    setLabel(field.label);
    setType(field.type);
    setRequired(field.required);
    setOptions(normalizeOptions(field.options));
    const s = field.settings ?? {};
    setPlaceholder(s.placeholder ?? "");
    setHelpText(s.helpText ?? "");
    setDefaultValue(s.defaultValue ?? "");
    setValidation(s.validation ?? {});
    setPrimaryDate(s.primaryDate ?? false);
    setShowOnCalendar(s.showOnCalendar ?? false);
    // Seed scope draft from the field. Empty arrays mean "no scope" —
    // backend treats null + empty interchangeably.
    setScopeDraft({
      scopeTagIds: field.scopeTagIds ?? [],
      scopeClientIds: field.scopeClientIds ?? [],
      scopeCategories: field.scopeCategories ?? [],
    });
  }, [field]);

  const save = useMutation({
    mutationFn: () => {
      if (!field) throw new Error("no_field");
      const settings: CustomFieldSettings = {
        placeholder: placeholder.trim() || undefined,
        helpText: helpText.trim() || undefined,
        defaultValue: defaultValue.trim() || undefined,
        validation: cleanValidation(validation),
        // Date-field calendar/main-date flags are meaningful only for
        // per-service DATE fields.
        primaryDate: isDateLike(type) && !scopeBody && primaryDate ? true : undefined,
        showOnCalendar: isDateLike(type) && !scopeBody && showOnCalendar ? true : undefined,
      };
      // Only send `options` when the field is (or has become) a select —
      // otherwise leave the column untouched. Always send `settings`.
      const sendOptions = type === "select" ? options : undefined;
      // Fold whatever scope keys the consumer wired into the save payload.
      // Backend treats null/empty arrays interchangeably as "no scope."
      const scopePayload: Partial<CustomFieldInput> = scopeBody
        ? {
            ...(scopeDraft.scopeTagIds !== undefined && {
              scopeTagIds: scopeDraft.scopeTagIds,
            }),
            ...(scopeDraft.scopeClientIds !== undefined && {
              scopeClientIds: scopeDraft.scopeClientIds,
            }),
            ...(scopeDraft.scopeCategories !== undefined && {
              scopeCategories: scopeDraft.scopeCategories,
            }),
          }
        : {};
      return onSave({
        label: label.trim(),
        type,
        required,
        options: sendOptions,
        settings: cleanSettings(settings),
        ...scopePayload,
      });
    },
    onSuccess: () => {
      onSaveSuccess?.();
      toast.success("Field saved");
      onClose();
    },
    onError: (e) => toast.error("Couldn't save field", (e as Error).message),
  });

  const tabs = useMemo(() => {
    const base: { key: TabKey; label: string }[] = [
      { key: "basic", label: "Basic" },
    ];
    if (type === "select") base.push({ key: "options", label: "Options" });
    base.push({ key: "advanced", label: "Advanced" });
    base.push({ key: "validation", label: "Validation" });
    if (scopeBody) {
      base.push({ key: "scope", label: scopeBody.tabLabel ?? "Scope" });
    }
    return base;
  }, [type, scopeBody]);

  // If the operator switches away from `select`, the Options tab disappears
  // — bounce the active tab back to Basic so it doesn't render a gap.
  useEffect(() => {
    if (tab === "options" && type !== "select") setTab("basic");
  }, [tab, type]);

  // If the consumer pulls `scopeBody` away mid-edit (e.g., re-render),
  // bounce the active tab back to Basic so we don't strand the user.
  useEffect(() => {
    if (tab === "scope" && !scopeBody) setTab("basic");
  }, [tab, scopeBody]);

  return (
    <Drawer open={open} onClose={onClose} width="lg">
      {field && (
        <>
          <DrawerHeader
            title={
              <span className="truncate">{label || "Untitled field"}</span>
            }
            subtitle={`Type: ${type}`}
            onClose={onClose}
          />
          <Tabs<TabKey>
            value={tab}
            onChange={setTab}
            className="mt-3 px-5"
            items={tabs}
          />

          <DrawerBody className="px-5 py-5 space-y-4">
            {tab === "basic" && (
              <BasicTab
                label={label}
                onLabel={setLabel}
                type={type}
                onType={setType}
                required={required}
                onRequired={setRequired}
                showDateOptions={isDateLike(type) && !scopeBody}
                primaryDate={primaryDate}
                onPrimaryDate={setPrimaryDate}
                primaryDateTakenBy={primaryDateTakenBy ?? null}
                showOnCalendar={showOnCalendar}
                onShowOnCalendar={setShowOnCalendar}
              />
            )}
            {tab === "options" && type === "select" && (
              <OptionsEditor options={options} onChange={setOptions} />
            )}
            {tab === "advanced" && (
              <AdvancedTab
                fieldType={type}
                placeholder={placeholder}
                onPlaceholder={setPlaceholder}
                helpText={helpText}
                onHelpText={setHelpText}
                defaultValue={defaultValue}
                onDefaultValue={setDefaultValue}
                options={options}
              />
            )}
            {tab === "validation" && (
              <ValidationTab
                fieldType={type}
                validation={validation}
                onChange={setValidation}
              />
            )}
            {tab === "scope" && scopeBody && field && (
              <div className="space-y-3">
                {scopeBody.helper && (
                  <p className="text-xs text-muted-foreground">
                    {scopeBody.helper}
                  </p>
                )}
                {scopeBody.render({
                  field,
                  draft: scopeDraft,
                  setDraft: setScopeDraft,
                })}
              </div>
            )}
          </DrawerBody>

          <DrawerFooter>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => save.mutate()}
              disabled={save.isPending || !label.trim()}
            >
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </DrawerFooter>
        </>
      )}
    </Drawer>
  );
}

function BasicTab({
  label,
  onLabel,
  type,
  onType,
  required,
  onRequired,
  showDateOptions,
  primaryDate,
  onPrimaryDate,
  primaryDateTakenBy,
  showOnCalendar,
  onShowOnCalendar,
}: {
  label: string;
  onLabel: (v: string) => void;
  type: CustomFieldType;
  onType: (v: CustomFieldType) => void;
  required: boolean;
  onRequired: (v: boolean) => void;
  showDateOptions: boolean;
  primaryDate: boolean;
  onPrimaryDate: (v: boolean) => void;
  primaryDateTakenBy: string | null;
  showOnCalendar: boolean;
  onShowOnCalendar: (v: boolean) => void;
}) {
  // Block enabling a second MAIN date, but always allow turning THIS one off
  // (so the operator can move the main-date flag to another field).
  const lockedByOther = !primaryDate && !!primaryDateTakenBy;
  return (
    <section className="rounded-lg border bg-card">
      <div className="px-4 py-2.5 border-b border-border">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          Field basics
        </p>
      </div>
      <div className="px-4 py-4 space-y-3">
        <div className="grid gap-1.5">
          <Label htmlFor="field-label">Label</Label>
          <Input
            id="field-label"
            value={label}
            onChange={(e) => onLabel(e.target.value)}
            placeholder="e.g. Venue, Guest count, Photographer preference"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="field-type">Type</Label>
          <Select
            value={type}
            onValueChange={(v) => onType(v as CustomFieldType)}
          >
            <SelectTrigger id="field-type" className="h-10 capitalize">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((ft) => (
                <SelectItem key={ft.value} value={ft.value}>
                  {ft.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div>
            <p className="text-sm font-medium">Required</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Customer must fill this in to book.
            </p>
          </div>
          <PanelToggle
            checked={required}
            onChange={onRequired}
            ariaLabel="Required toggle"
          />
        </div>
        {showDateOptions && (
          <>
            <div className="flex items-center justify-between pt-1 border-t border-border">
              <div className="pr-4">
                <p className="text-sm font-medium">Show on calendar</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Place this date on the operator calendar. You can turn this on
                  for several date fields.
                </p>
              </div>
              <PanelToggle
                checked={showOnCalendar}
                onChange={onShowOnCalendar}
                ariaLabel="Show on calendar toggle"
              />
            </div>
            <div className="flex items-center justify-between pt-1">
              <div className="pr-4">
                <p className="text-sm font-medium">Use as main date</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {lockedByOther
                    ? `Already set on “${primaryDateTakenBy}”. Turn it off there first.`
                    : "Shown in the Bookings list Date column. One per service."}
                </p>
              </div>
              <PanelToggle
                checked={primaryDate}
                onChange={onPrimaryDate}
                disabled={lockedByOther}
                ariaLabel="Use as main date toggle"
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function OptionsEditor({
  options,
  onChange,
}: {
  options: SelectOption[];
  onChange: (v: SelectOption[]) => void;
}) {
  const update = (i: number, patch: Partial<SelectOption>) => {
    const next = options.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const move = (i: number, dir: -1 | 1) => {
    const target = i + dir;
    if (target < 0 || target >= options.length) return;
    const next = options.slice();
    [next[i], next[target]] = [next[target], next[i]];
    onChange(next);
  };
  const remove = (i: number) => {
    const next = options.slice();
    next.splice(i, 1);
    onChange(next);
  };
  const add = () => {
    onChange([...options, { value: "", label: "" }]);
  };

  return (
    <section className="rounded-lg border bg-card">
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            Options
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Each option has a stored value + a display label so renames don't
            break stored booking values.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={add}>
          <Plus className="h-3 w-3 mr-1" /> Add option
        </Button>
      </div>

      {options.length === 0 ? (
        <EmptyState
          bare
          icon={Plus}
          title="No options yet"
          description="Add at least one option for the customer to pick from."
          action={
            <Button size="sm" onClick={add}>
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add your first option
            </Button>
          }
        />
      ) : (
        <ul className="divide-y">
          {options.map((opt, i) => (
            <li key={i} className="flex items-center gap-2 px-3 py-2">
              <div className="flex flex-col items-center gap-0.5 text-muted-foreground">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="h-4 w-4 inline-flex items-center justify-center rounded hover:bg-muted disabled:opacity-30"
                  aria-label="Move up"
                >
                  <ChevronUp className="h-3 w-3" />
                </button>
                <GripVertical className="h-3.5 w-3.5 opacity-40" />
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === options.length - 1}
                  className="h-4 w-4 inline-flex items-center justify-center rounded hover:bg-muted disabled:opacity-30"
                  aria-label="Move down"
                >
                  <ChevronDown className="h-3 w-3" />
                </button>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-2 min-w-0">
                <Input
                  value={opt.label}
                  onChange={(e) => {
                    const newLabel = e.target.value;
                    // Auto-fill the stored value from the label until the
                    // operator hand-edits it (then leave value alone).
                    const wasAutoValue = slugify(opt.label) === opt.value;
                    update(i, {
                      label: newLabel,
                      value: wasAutoValue ? slugify(newLabel) : opt.value,
                    });
                  }}
                  placeholder="Display label (e.g. Small)"
                />
                <Input
                  value={opt.value}
                  onChange={(e) => update(i, { value: e.target.value })}
                  placeholder="Stored value (e.g. small)"
                />
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition"
                aria-label="Remove option"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AdvancedTab({
  fieldType,
  placeholder,
  onPlaceholder,
  helpText,
  onHelpText,
  defaultValue,
  onDefaultValue,
  options,
}: {
  fieldType: CustomFieldType;
  placeholder: string;
  onPlaceholder: (v: string) => void;
  helpText: string;
  onHelpText: (v: string) => void;
  defaultValue: string;
  onDefaultValue: (v: string) => void;
  options: SelectOption[];
}) {
  const supportsPlaceholder =
    fieldType === "text" ||
    fieldType === "textarea" ||
    fieldType === "number" ||
    isDateLike(fieldType);

  return (
    <section className="rounded-lg border bg-card">
      <div className="px-4 py-2.5 border-b border-border">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          Advanced
        </p>
      </div>
      <div className="px-4 py-4 space-y-3">
        {supportsPlaceholder ? (
          <div className="grid gap-1.5">
            <Label htmlFor="field-placeholder">Placeholder</Label>
            <Input
              id="field-placeholder"
              value={placeholder}
              onChange={(e) => onPlaceholder(e.target.value)}
              placeholder="Hint text shown inside the empty input"
            />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">
            Placeholder is not used for {fieldType} fields.
          </p>
        )}
        <div className="grid gap-1.5">
          <Label htmlFor="field-help">Help text</Label>
          <textarea
            id="field-help"
            rows={2}
            value={helpText}
            onChange={(e) => onHelpText(e.target.value)}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="Shown under the input — explain what to enter."
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="field-default">Default value</Label>
          {fieldType === "select" ? (
            <Select
              value={defaultValue || undefined}
              onValueChange={onDefaultValue}
            >
              <SelectTrigger id="field-default" className="h-10">
                <SelectValue placeholder="(no default)" />
              </SelectTrigger>
              <SelectContent>
                {options.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : fieldType === "checkbox" ? (
            <Select
              value={defaultValue || undefined}
              onValueChange={onDefaultValue}
            >
              <SelectTrigger id="field-default" className="h-10">
                <SelectValue placeholder="(unchecked)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Checked by default</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Input
              id="field-default"
              type={
                fieldType === "number"
                  ? "number"
                  : fieldType === "date"
                    ? "date"
                    : fieldType === "datetime"
                      ? "datetime-local"
                      : "text"
              }
              value={defaultValue}
              onChange={(e) => onDefaultValue(e.target.value)}
            />
          )}
          <p className="text-[11px] text-muted-foreground">
            Pre-fills the booking form. Customers can change it before submit.
          </p>
        </div>
      </div>
    </section>
  );
}

function ValidationTab({
  fieldType,
  validation,
  onChange,
}: {
  fieldType: CustomFieldType;
  validation: CustomFieldValidation;
  onChange: (v: CustomFieldValidation) => void;
}) {
  const patch = (p: Partial<CustomFieldValidation>) => onChange({ ...validation, ...p });

  if (fieldType === "text" || fieldType === "textarea") {
    return (
      <section className="rounded-lg border bg-card">
        <div className="px-4 py-2.5 border-b border-border">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            Validation
          </p>
        </div>
        <div className="px-4 py-4 space-y-3">
          <div className="grid gap-1.5">
            <Label htmlFor="field-pattern">Pattern (regex)</Label>
            <Input
              id="field-pattern"
              value={validation.pattern ?? ""}
              onChange={(e) => patch({ pattern: e.target.value || undefined })}
              placeholder={"^\\d{4}$"}
            />
            <p className="text-[11px] text-muted-foreground">
              Booking submission is rejected when the value doesn't match.
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (fieldType === "number") {
    return (
      <section className="rounded-lg border bg-card">
        <div className="px-4 py-2.5 border-b border-border">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            Validation
          </p>
        </div>
        <div className="px-4 py-4 grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="field-min">Min</Label>
            <Input
              id="field-min"
              type="number"
              value={validation.min ?? ""}
              onChange={(e) =>
                patch({
                  min: e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="field-max">Max</Label>
            <Input
              id="field-max"
              type="number"
              value={validation.max ?? ""}
              onChange={(e) =>
                patch({
                  max: e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
            />
          </div>
        </div>
      </section>
    );
  }

  if (fieldType === "file") {
    return (
      <section className="rounded-lg border bg-card">
        <div className="px-4 py-2.5 border-b border-border">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            Validation
          </p>
        </div>
        <div className="px-4 py-4 space-y-3">
          <div className="grid gap-1.5">
            <Label htmlFor="field-types">Allowed file types</Label>
            <Input
              id="field-types"
              value={(validation.fileTypes ?? []).join(", ")}
              onChange={(e) =>
                patch({
                  fileTypes: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              placeholder=".pdf, .png, .jpg"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="field-max-size">Max size (MB)</Label>
            <Input
              id="field-max-size"
              type="number"
              min={0}
              value={validation.maxFileSizeMb ?? ""}
              onChange={(e) =>
                patch({
                  maxFileSizeMb:
                    e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
            />
          </div>
          <p className="text-[11px] text-muted-foreground italic">
            Stored now; enforced when file uploads land for bookings.
          </p>
        </div>
      </section>
    );
  }

  return (
    <EmptyState
      bare
      icon={Plus}
      title="No validation rules for this type"
      description="Switch the field type to text, textarea, number, or file to configure validation."
    />
  );
}

function PanelToggle({
  checked,
  onChange,
  ariaLabel,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <Switch
      checked={checked}
      onCheckedChange={onChange}
      disabled={disabled}
      aria-label={ariaLabel}
    />
  );
}

function slugify(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function cleanValidation(v: CustomFieldValidation): CustomFieldValidation | undefined {
  const out: CustomFieldValidation = {};
  if (v.min != null && Number.isFinite(v.min)) out.min = v.min;
  if (v.max != null && Number.isFinite(v.max)) out.max = v.max;
  if (v.pattern && v.pattern.trim()) out.pattern = v.pattern.trim();
  if (v.fileTypes && v.fileTypes.length > 0) out.fileTypes = v.fileTypes;
  if (v.maxFileSizeMb != null && Number.isFinite(v.maxFileSizeMb))
    out.maxFileSizeMb = v.maxFileSizeMb;
  return Object.keys(out).length ? out : undefined;
}

function cleanSettings(s: CustomFieldSettings): CustomFieldSettings | null {
  const out: CustomFieldSettings = {};
  if (s.placeholder) out.placeholder = s.placeholder;
  if (s.helpText) out.helpText = s.helpText;
  if (s.defaultValue) out.defaultValue = s.defaultValue;
  if (s.validation) out.validation = s.validation;
  if (s.primaryDate) out.primaryDate = true;
  if (s.showOnCalendar) out.showOnCalendar = true;
  return Object.keys(out).length ? out : null;
}
